import React, { useState, useEffect } from 'react';
import { Modal } from '@/components/ui/modal';
import Button from '@/components/ui/button/Button';
import Label from '@/components/form/Label';
import Input from '@/components/form/input/InputField';
import ReactDatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { ClientVerifyData } from '../api/clientVerificationApi';
import { useClientVerification } from '../hooks/useClientVerification';
import { env } from '@/shared/config/env';
import { useToast } from '@/components/ui/toast/Toast';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  clientId: string;
}

interface FormErrors {
  passport_document_number?: boolean;
  passport_issued_at?: boolean;
  passport_valid_till?: boolean;
  driver_license_document_number?: boolean;
  driver_license_issued_at?: boolean;
  driver_license_valid_till?: boolean;
}

const COUNTDOWN_TIME: number = Number(env.delayBeforeVerification || 0); // seconds

export const ClientVerificationModal: React.FC<Props> = ({
  isOpen,
  onClose,
  clientId,
}) => {
  const toast = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [formData, setFormData] = useState<ClientVerifyData>({
    passport_document_number: '',
    passport_issued_at: '',
    passport_valid_till: '',
    driver_license_document_number: '',
    driver_license_issued_at: '',
    driver_license_valid_till: '',
  });
  const [isFormDisabled, setIsFormDisabled] = useState(true);
  const [showCountdown, setShowCountdown] = useState(false);
  const [countdown, setCountdown] = useState(COUNTDOWN_TIME);
  const [hasDownloadedArchive, setHasDownloadedArchive] = useState(false);

  const {
    getDocumentsArchiveUrl,
    acceptVerification,
    declineVerification
  } = useClientVerification(clientId);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (showCountdown && countdown > 0) {
      timer = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            setShowCountdown(false);
            setIsFormDisabled(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [showCountdown, countdown]);

  const resetForm = () => {
    setFormData({
      passport_document_number: '',
      passport_issued_at: '',
      passport_valid_till: '',
      driver_license_document_number: '',
      driver_license_issued_at: '',
      driver_license_valid_till: '',
    });
    setErrors({
      passport_document_number: false,
      passport_issued_at: false,
      passport_valid_till: false,
      driver_license_document_number: false,
      driver_license_issued_at: false,
      driver_license_valid_till: false,
    });
    setIsFormDisabled(true);
    setShowCountdown(false);
    setCountdown(COUNTDOWN_TIME);
    setHasDownloadedArchive(false);
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    let countErrors = 0;

    // Check if fields are not empty
    Object.entries(formData).forEach(([key, value]) => {
      newErrors[key as keyof FormErrors] = (typeof value !== 'string' || value === '') ? true : false;
      if (newErrors[key as keyof FormErrors]) {
        countErrors++;
      }
    });

    setErrors(newErrors);
    return countErrors === 0;
  };

  const handleDownloadArchive = async () => {
    try {
      const response = await getDocumentsArchiveUrl.mutateAsync();
      const link = document.createElement('a');
      link.href = response.url;
      link.click();
      setHasDownloadedArchive(true);
      setShowCountdown(true);
    } catch (error) {
      toast.error('Failed to download archive'  + `<br/><br/>${error}`);
    }
  };

  const handleBlur = (_: React.FocusEvent<HTMLInputElement>) => {
    validateForm();
  };

  const handleVerify = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      await acceptVerification.mutateAsync(formData);
      onClose();
      resetForm();
      toast.success('Client verified successfully');
    } catch (error) {
      toast.error('Failed to verify client' + `<br/><br/>${error}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDecline = async () => {
    setIsSubmitting(true);
    try {
      await declineVerification.mutateAsync();
      onClose();
      resetForm();
      toast.success('Verification declined');
    } catch (error) {
      toast.error('Failed to decline verification' + `<br/><br/>${error}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDateChangeRaw = (value: string) => {
    const [day, month, year] = value.split('.');
    if (day && month && year) {
      const date = new Date(`${year}-${month}-${day}`);
      if (date instanceof Date && !isNaN(date.getTime())) {
        return date;
      }
    }
    return null;
  };

  const getDatePickerClassName = (hasError: boolean | undefined) => {
    return `w-full rounded-lg ${hasError ? 'border-error-500 dark:border-error-500' : 'border-gray-300 dark:border-gray-700'} bg-transparent py-[9px] px-4 outline-none transition focus:border-primary active:border-primary disabled:cursor-default disabled:bg-whiter dark:bg-form-input dark:focus:border-primary`;
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={() => {
          resetForm();
          onClose();
        }}
        className="max-w-[600px] p-6"
      >
        <style>
          {`
            .react-datepicker-wrapper {
              width: 100%;
            }

            .react-datepicker-popper {
              z-index: 20;
            }

            .react-datepicker__month-container {
              float: none !important;
              min-height: 310px !important;
            }

            /* Стили для мобильных устройств */
            @media (max-width: 767px) {
              .react-datepicker-popper {
                position: fixed !important;
                top: auto !important;
                bottom: 0 !important;
                left: 0 !important;
                right: 0 !important;
                transform: none !important;
                margin: 1rem !important;
                width: calc(100% - 2rem) !important;
              }

              .react-datepicker {
                width: 100% !important;
              }

              .react-datepicker__month-container {
                width: 100% !important;
                float: none !important;
              }

              .react-datepicker__day {
                width: calc((100% - 2.8rem) / 7) !important;
                line-height: 2.5rem !important;
                margin: 0.2rem !important;
              }

              .react-datepicker__day-name {
                width: calc((100% - 2.8rem) / 7) !important;
                margin: 0.2rem !important;
              }
            }

            /* Стили для десктопа */
            @media (min-width: 768px) {
              .react-datepicker-wrapper {
                position: relative;
              }

              .react-datepicker-popper {
                z-index: 20;
                margin-top: 0.1rem !important;
              }

              .react-datepicker-popper[data-placement^='bottom-start'] {
                margin-left: 0rem !important;
              }

              .react-datepicker-popper[data-placement^='bottom-end'] {
                margin-right: 0.5rem !important;
              }
            }

            .react-datepicker-popper[data-placement^='bottom'] .react-datepicker__triangle {
              display: none;
            }

            .react-datepicker {
              font-family: inherit;
              border: 1px solid #e5e7eb !important;
              border-radius: 1rem;
              box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
              font-size: 0.875rem;
            }

            .react-datepicker__header {
              background-color: transparent;
              border-bottom: 1px solid #e5e7eb;
              border-radius: 1rem 1rem 0 0;
              padding: 0.75rem 0;
            }

            .react-datepicker__current-month {
              font-weight: 600;
              font-size: 0.875rem;
              color: #1f2937;
              margin-bottom: 0.5rem;
            }

            .react-datepicker__day-name {
              color: #6b7280;
              font-weight: 500;
              font-size: 0.75rem;
              width: 2rem;
              margin: 0.2rem;
            }

            .react-datepicker__day {
              color: #1f2937;
              border-radius: 0.5rem;
              margin: 0.2rem;
              width: 2rem;
              line-height: 2rem;
              font-size: 0.875rem;
            }

            .react-datepicker__day:hover {
              background-color: #f3f4f6;
              border-radius: 0.5rem;
            }

            .react-datepicker__day--selected,
            .react-datepicker__day--keyboard-selected {
              background-color: #3b82f6 !important;
              border-radius: 0.5rem;
              color: white !important;
            }

            .react-datepicker__day--disabled {
              color: #9ca3af;
            }

            .react-datepicker__navigation {
              top: 0.75rem;
            }

            .react-datepicker__navigation-icon::before {
              border-color: #6b7280;
              border-width: 2px 2px 0 0;
              height: 8px;
              width: 8px;
            }

            .dark .react-datepicker {
              background-color: #1e293b;
              border-color: #334155 !important;
            }

            .dark .react-datepicker__header {
              background-color: #1e293b;
              border-color: #334155;
            }

            .dark .react-datepicker__current-month {
              color: #e5e7eb;
            }

            .dark .react-datepicker__day-name {
              color: #9ca3af;
            }

            .dark .react-datepicker__day {
              color: #e5e7eb;
            }

            .dark .react-datepicker__day:hover {
              background-color: #334155;
            }

            .dark .react-datepicker__day--disabled {
              color: #4b5563;
            }

            .dark .react-datepicker__navigation-icon::before {
              border-color: #9ca3af;
            }
          `}
        </style>

        <div className="md:m-8">
          <h2 className="text-xl font-semibold mb-4 dark:text-white">
            Verify Client Documents
          </h2>

          <div className="space-y-4">
            <div className="mb-6">
              <Button
                variant="primary"
                onClick={handleDownloadArchive}
                disabled={hasDownloadedArchive}
              >
                Download Archive
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Passport Section */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium dark:text-white">Passport</h3>

                <div>
                  <Label>Passport Number</Label>
                  <Input
                    type="text"
                    value={formData.passport_document_number}
                    onChange={(e) => setFormData(prev => ({ ...prev, passport_document_number: e.target.value }))}
                    placeholder="Enter passport number"
                    error={errors.passport_document_number}
                    disabled={isFormDisabled}
                    onBlur={handleBlur}
                  />
                </div>

                <div>
                  <Label>Passport Issue Date</Label>
                  <ReactDatePicker
                    selected={formData.passport_issued_at ? new Date(formData.passport_issued_at) : null}
                    onChange={(date: Date | null) => setFormData(prev => ({
                      ...prev,
                      passport_issued_at: date ? date.toISOString().split('T')[0] : ''
                    }))}
                    dateFormat="dd.MM.yyyy"
                    className={getDatePickerClassName(errors.passport_issued_at)}
                    disabled={isFormDisabled}
                    onBlur={handleBlur}
                    onChangeRaw={(e: any) => {
                      if (e?.target?.value) {
                        const date = handleDateChangeRaw(e.target.value);
                        if (date) {
                          setFormData(prev => ({
                            ...prev,
                            passport_issued_at: date.toISOString().split('T')[0]
                          }));
                        }
                      }
                    }}
                  />
                </div>

                <div>
                  <Label>Passport Valid Till</Label>
                  <ReactDatePicker
                    selected={formData.passport_valid_till ? new Date(formData.passport_valid_till) : null}
                    onChange={(date: Date | null) => setFormData(prev => ({
                      ...prev,
                      passport_valid_till: date ? date.toISOString().split('T')[0] : ''
                    }))}
                    dateFormat="dd.MM.yyyy"
                    className={getDatePickerClassName(errors.passport_valid_till)}
                    disabled={isFormDisabled}
                    onBlur={handleBlur}
                    onChangeRaw={(e: any) => {
                      if (e?.target?.value) {
                        const date = handleDateChangeRaw(e.target.value);
                        if (date) {
                          setFormData(prev => ({
                            ...prev,
                            passport_valid_till: date.toISOString().split('T')[0]
                          }));
                        }
                      }
                    }}
                  />
                </div>
              </div>

              {/* Driver's License Section */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium dark:text-white">Driver's License</h3>

                <div>
                  <Label>License Number</Label>
                  <Input
                    type="text"
                    value={formData.driver_license_document_number}
                    onChange={(e) => setFormData(prev => ({ ...prev, driver_license_document_number: e.target.value }))}
                    placeholder="Enter license number"
                    error={errors.driver_license_document_number}
                    disabled={isFormDisabled}
                    onBlur={handleBlur}
                  />
                </div>

                <div>
                  <Label>License Issue Date</Label>
                  <ReactDatePicker
                    selected={formData.driver_license_issued_at ? new Date(formData.driver_license_issued_at) : null}
                    onChange={(date: Date | null) => setFormData(prev => ({
                      ...prev,
                      driver_license_issued_at: date ? date.toISOString().split('T')[0] : ''
                    }))}
                    dateFormat="dd.MM.yyyy"
                    className={getDatePickerClassName(errors.driver_license_issued_at)}
                    disabled={isFormDisabled}
                    onBlur={handleBlur}
                    onChangeRaw={(e: any) => {
                      if (e?.target?.value) {
                        const date = handleDateChangeRaw(e.target.value);
                        if (date) {
                          setFormData(prev => ({
                            ...prev,
                            driver_license_issued_at: date.toISOString().split('T')[0]
                          }));
                        }
                      }
                    }}
                  />
                </div>

                <div>
                  <Label>License Valid Till</Label>
                  <ReactDatePicker
                    selected={formData.driver_license_valid_till ? new Date(formData.driver_license_valid_till) : null}
                    onChange={(date: Date | null) => setFormData(prev => ({
                      ...prev,
                      driver_license_valid_till: date ? date.toISOString().split('T')[0] : ''
                    }))}
                    dateFormat="dd.MM.yyyy"
                    className={getDatePickerClassName(errors.driver_license_valid_till)}
                    disabled={isFormDisabled}
                    onBlur={handleBlur}
                    onChangeRaw={(e: any) => {
                      if (e?.target?.value) {
                        const date = handleDateChangeRaw(e.target.value);
                        if (date) {
                          setFormData(prev => ({
                            ...prev,
                            driver_license_valid_till: date.toISOString().split('T')[0]
                          }));
                        }
                      }
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-4 mt-6">
              <Button
                variant="danger"
                onClick={handleDecline}
                disabled={isSubmitting || !hasDownloadedArchive}
              >
                Decline
              </Button>
              <Button
                variant="primary"
                onClick={handleVerify}
                disabled={isSubmitting || isFormDisabled}
              >
                Verify
              </Button>
            </div>
          </div>
        </div>
      </Modal>

      {/* Countdown Modal */}
      <Modal
        isOpen={showCountdown}
        onClose={() => {}}
        className="max-w-[300px] p-6"
        showCloseButton={false}
      >
        <div className="text-center">
          <h3 className="text-lg font-medium mb-4 dark:text-white">
            Please review the documents from the archive
          </h3>
          <p className="text-gray-500 dark:text-gray-400">
            Form will be available in {Math.floor(countdown / 60)}:{String(countdown % 60).padStart(2, '0')}
          </p>
        </div>
      </Modal>
    </>
  );
};
